import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';

import { FaCheck, FaPlus, FaTimes } from 'react-icons/fa';

import classNames from 'classnames';
import AppCard from '../../../../../components/appCard';
import { LargeTablePague } from '../../../../../styles/primeReactConfig';
import { confirmFooter } from '../../../../../components/confirmFooter';
import { useInfo } from '../../../../../context/infoContext';
import { ApiAdimplencia } from '../../../../../services/api';
import useTerminologiaRecorrencia from '../../../../../hooks/useTerminologiaRecorrencia';

const DEBOUNCE_MS = 400;

export default function GerenciarGrupos() {
  const terminologia = useTerminologiaRecorrencia();
  const [loading, setIsLoading] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [grupoDescricao, setGrupoDescricao] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showUpdate, setShowUpdate] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [grupoData, setGrupoData] = useState({});
  const [type, setType] = useState('');

  const { credorData } = useInfo();

  const fetchGrupos = useCallback(async (pageNum = 0, limit = rows, searchTerm = '') => {
    setIsLoading(true);
    if (!credorData) {
      setIsLoading(false);
      return;
    }
    try {
      const params = new URLSearchParams({ page: pageNum, limit });
      if (searchTerm && searchTerm.trim()) params.set('search', searchTerm.trim());
      const { data } = await ApiAdimplencia.get(`/credorgrupo?${params.toString()}`);
      const dados = data?.dados || data;
      setGrupos(dados?.results || []);
      setTotalRegistros(dados?.total_registros || 0);
    } catch (error) {
      console.error(error?.response);
      toast.error('Houve um erro ao carregar a listagem.');
      setGrupos([]);
    } finally {
      setIsLoading(false);
    }
  }, [credorData, rows]);

  useEffect(() => {
    fetchGrupos(page, rows, search);
  }, [fetchGrupos, page, rows, search]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
    setFirst(0);
  }, [search]);

  function onPage(event) {
    setPage(event.page);
    setRows(event.rows);
    setFirst(event.first);
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (!grupoDescricao?.trim()) {
      toast.error(`Informe a descrição de ${terminologia.labelGrupo.toLowerCase()}.`);
      return;
    }
    if (grupoDescricao.trim().length > 45) {
      toast.error('Descrição deve ter no máximo 45 caracteres.');
      return;
    }
    setGrupoData({ grupo_descricao: grupoDescricao.trim(), grupo_status: true });
    setShowConfirm('C');
  }

  function handleUpdate(event) {
    event.preventDefault();
    if (type === 'U') {
      setGrupoData({
        grupo_descricao: grupoDescricao.trim(),
        grupo_status: showUpdate?.grupo_status ?? true,
        credor_grupo_id: showUpdate?.credor_grupo_id
      });
      setShowConfirm('U');
    } else {
      setGrupoData({ credor_grupo_id: showUpdate?.credor_grupo_id });
      setShowConfirm('D');
    }
  }

  async function cadastraGrupo() {
    setIsLoading(true);
    try {
      await ApiAdimplencia.post('/credorgrupo/inserir', grupoData);
      toast.success('Cadastro realizado com sucesso!');
      setShowCreate(false);
      setGrupoDescricao('');
      setGrupoData({});
      setShowConfirm(false);
      fetchGrupos(page, rows, search);
    } catch (error) {
      toast.error(
        error?.response?.data?.mensagem || 'Não foi possível concluir o cadastro.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function atualizaGrupo() {
    setIsLoading(true);
    try {
      await ApiAdimplencia.put('/credorgrupo/atualizar', grupoData);
      toast.success('Atualização realizada com sucesso!');
      setShowUpdate(false);
      setGrupoDescricao('');
      setGrupoData({});
      setShowConfirm(false);
      fetchGrupos(page, rows, search);
    } catch (error) {
      toast.error(
        error?.response?.data?.mensagem || 'Não foi possível concluir a atualização.'
      );
    } finally {
      setIsLoading(false);
    }
  }

  async function removeGrupo() {
    setIsLoading(true);
    try {
      await ApiAdimplencia.delete('/credorgrupo/deletar', { data: grupoData });
      toast.success('Remoção realizada com sucesso!');
      setShowUpdate(false);
      setGrupoData({});
      setShowConfirm(false);
      fetchGrupos(page, rows, search);
    } catch (error) {
      if (error?.response?.status === 402) {
        toast.info(
          'Não é possível remover. Existem clientes vinculados.'
        );
      } else {
        toast.error(
          error?.response?.data?.mensagem || 'Não foi possível concluir a remoção.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }

  const statusTemplate = (row) => (
    <span
      className={classNames(
        'text-nowrap py-1.5 px-3 rounded-md text-sm font-medium inline-flex items-center',
        row.grupo_status
          ? 'bg-emerald-500/20 text-emerald-600 dark:bg-emerald-500/30 dark:text-emerald-400'
          : 'bg-red-500/20 text-red-600 dark:bg-red-500/30 dark:text-red-400'
      )}
    >
      {row.grupo_status ? 'Ativo' : 'Inativo'}
    </span>
  );

  return (
    <div className="grid">
      {showConfirm && (
        <ConfirmDialog
          onHide={() => setShowConfirm(false)}
          header={`Deseja ${showConfirm === 'C' ? 'cadastrar' : showConfirm === 'U' ? 'atualizar' : 'remover'} ${terminologia.labelGrupo.toLowerCase()}?`}
          message={grupoData?.grupo_descricao || ''}
          acceptClassName="outline-green-500 hover:outline-green-600 outline-2 outline bg-green-500 hover:bg-green-600 rounded-md w-full py-3 text-white transition duration-300"
          rejectClassName="outline-red-500 outline-2 outline rounded-md w-full py-3 text-red-500 hover:bg-red-500 hover:text-white transition duration-300"
          acceptLabel="Sim"
          rejectLabel="Não"
          acceptIcon={<FaCheck />}
          rejectIcon={<FaTimes />}
          accept={() => {
            if (showConfirm === 'C') cadastraGrupo();
            else if (showConfirm === 'U') atualizaGrupo();
            else removeGrupo();
          }}
          reject={() => setShowConfirm(false)}
          pt={{ root: { className: 'lg:w-1/4 md:w-1/2 w-full' } }}
          footer={confirmFooter}
          visible
        />
      )}

      {showCreate && (
        <Dialog
          header={terminologia.tituloCadastrarGrupo}
          onHide={() => {
            setShowCreate(false);
            setGrupoDescricao('');
          }}
          pt={{ root: { className: 'lg:w-1/3 md:w-1/2 w-full' } }}
          visible
        >
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="grupo_descricao" className="text-sm font-medium text-gray-700 dark:text-white/80">
                Descrição
              </label>
              <InputText
                id="grupo_descricao"
                placeholder={terminologia.placeholderExemploGrupo}
                value={grupoDescricao}
                onChange={(e) => setGrupoDescricao(e.target.value)}
                type="text"
                required
                maxLength={45}
                className="w-full h-10 min-h-[2.5rem]"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                label="Enviar"
                type="submit"
                severity="success"
                className="h-10 min-h-[2.5rem] min-w-[6rem]"
              />
            </div>
          </form>
        </Dialog>
      )}

      {showUpdate && (
        <Dialog
          header={`${terminologia.labelGrupo}: ${showUpdate?.grupo_descricao || ''}`}
          pt={{ root: { className: 'lg:w-5/12 md:w-8/12 w-full' } }}
          onHide={() => {
            setGrupoDescricao('');
            setShowUpdate(false);
          }}
          onShow={() => setGrupoDescricao(showUpdate?.grupo_descricao || '')}
          visible
        >
          <form onSubmit={handleUpdate} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="grupo_descricao_edit" className="text-sm font-medium text-gray-700 dark:text-white/80">
                Descrição
              </label>
              <InputText
                className="w-full h-10 min-h-[2.5rem]"
                id="grupo_descricao_edit"
                placeholder={terminologia.placeholderExemploGrupo}
                value={grupoDescricao}
                onChange={(e) => setGrupoDescricao(e.target.value)}
                type="text"
                required
                maxLength={45}
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button
                className="h-10 min-h-[2.5rem] min-w-[6rem]"
                label="Atualizar"
                onClick={() => setType('U')}
                type="submit"
              />
              <Button
                className="h-10 min-h-[2.5rem] min-w-[6rem]"
                label="Deletar"
                severity="danger"
                onClick={() => setType('D')}
                type="submit"
              />
            </div>
          </form>
        </Dialog>
      )}

      <div className="w-full">
        <AppCard className="!p-0 flex flex-col">
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-gray-200 dark:border-blue-900/40 shrink-0">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90 shrink-0">{terminologia.labelGruposNoContratante}</h3>
              <span className="text-sm text-gray-500 dark:text-gray-400 shrink-0">
                {terminologia.quantidadeGruposTexto(totalRegistros)}
              </span>
              <span className="flex-1 min-w-[140px] max-w-[220px]">
                <IconField iconPosition="left" className="w-full">
                  <InputIcon className="pi pi-search text-gray-500 dark:text-gray-400" />
                  <InputText
                    placeholder={terminologia.placeholderBuscarGrupo}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full h-9 text-sm"
                  />
                </IconField>
              </span>
            </div>
            <Button
              loading={loading}
              icon={<FaPlus className="mr-2" />}
              label={terminologia.acaoAdicionarGrupo}
              severity="success"
              type="button"
              className="h-10 min-h-[2.5rem] shrink-0"
              onClick={() => setShowCreate(true)}
            />
          </div>
          <div className="flex-1 overflow-auto min-h-0">
          <DataTable
            value={grupos}
            loading={loading}
            paginator
            lazy
            first={first}
            rows={rows}
            totalRecords={totalRegistros}
            onPage={onPage}
            rowsPerPageOptions={[5, 10, 20]}
            emptyMessage="Nenhum registro cadastrado."
            currentPageReportTemplate="Exibindo {first} a {last} de {totalRecords}"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            pt={{
              ...LargeTablePague,
              table: 'table-auto border-collapse min-w-full',
              header: ({ props }) => ({
                className: classNames(
                  'bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-white/80',
                  'border-b border-gray-200 dark:border-blue-900/40 px-4 py-2.5 text-sm font-medium'
                )
              }),
              column: {
                headercell: { className: 'text-left py-2.5 px-4' },
                bodycell: ({ context }) => ({
                  className: classNames(
                    'bg-card dark:bg-card_dark',
                    'text-left border-0 border-b border-solid border-gray-300',
                    context?.size === 'small' ? 'p-2' : context?.size === 'large' ? 'p-5' : 'py-2.5 px-4',
                    'dark:text-white/80 dark:border-blue-900/40',
                    'cursor-pointer',
                    'transition duration-150',
                    'hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700/80 dark:hover:text-white'
                  )
                })
              },
              bodyrow: ({ context }) => ({
                className: classNames(
                  context.selected ? 'bg-blue-50 text-blue-700 dark:bg-blue-300' : 'bg-white text-gray-600 dark:bg-gray-900',
                  'transition duration-150'
                )
              })
            }}
            onSelectionChange={(e) => setShowUpdate(e.value)}
            selectionMode="single"
          >
            <Column field="grupo_descricao" header="Descrição" style={{ fontSize: 15, fontWeight: 'bold' }} />
            <Column field="grupo_status" header="Status" body={statusTemplate} style={{ width: '120px' }} />
          </DataTable>
          </div>
        </AppCard>
      </div>
    </div>
  );
}
